import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider
} from '@mui/material';
import { Edit as EditIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import permissionService from '../../services/permissionService';

const PermissionDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const [permission, setPermission] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPermission();
  }, [id]);

  const fetchPermission = async () => {
    try {
      setLoading(true);
      const data = await permissionService.getPermissionWithUsage(id);
      setPermission(data);
    } catch (error) {
      toast.error(t('common.loadError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}>{t('common.loading')}</Box>;
  }

  if (!permission) {
    return <Box sx={{ p: 3 }}>{t('common.notFound')}</Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/permissions')}
          >
            {t('common.back')}
          </Button>
          <Typography variant="h4">{t('permissions.viewPermission')}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/permissions/edit/${id}`)}
        >
          {t('permissions.editPermission')}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('common.basicInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.displayName')}
                  </Typography>
                  <Typography variant="h5">{permission.displayName}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('permissions_ext.nameIdentifier')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip label={permission.name} size="small" />
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.description')}
                  </Typography>
                  <Typography variant="body1">
                    {permission.description || t('common.noDescription')}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('permissions.resource')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip label={permission.resource} color="primary" size="small" />
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('permissions.action')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip label={permission.action} color="secondary" size="small" />
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.status')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip
                      label={permission.isActive ? t('common.active') : t('common.inactive')}
                      color={permission.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.createdAt')}
                  </Typography>
                  <Typography variant="body2">
                    {new Date(permission.createdAt).toLocaleDateString()}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.updatedAt')}
                  </Typography>
                  <Typography variant="body2">
                    {new Date(permission.updatedAt).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Usage Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('permissions_ext.usageStats')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('permissions_ext.usedByRoles')}
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {permission.roleCount || 0}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('permissions_ext.directlyAssigned')}
                  </Typography>
                  <Typography variant="h4" color="secondary">
                    {permission.userCount || 0}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Roles using this permission */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('permissions_ext.rolesUsingPerm', { count: permission.roleNames?.length || 0 })}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {permission.roleNames && permission.roleNames.length > 0 ? (
                <Box>
                  {permission.roleNames.map((roleName, index) => (
                    <Chip
                      key={index}
                      label={roleName}
                      size="small"
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('permissions_ext.noRolesUsePerm')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PermissionDetails;
